/**
 * Everything the site says about Trustpilot, in one place.
 *
 * WHY THERE IS NO SCORE HERE. The badges used to hard-code "4.9 Excellent,
 * 932+ reviews". The real profile, checked 25 October 2026, shows a TrustScore
 * of 2.6 from 32 reviews. Since the badges did not link anywhere, nobody could
 * see the gap, and linking them would have shown it to everyone who clicked.
 * Publishing review information that misrepresents the real picture is also
 * banned outright under the DMCC Act 2024.
 *
 * So the site links to the profile, shows how many five-star reviews it has
 * and a labelled selection of them, and makes no claim about the overall
 * rating. If a score is ever
 * shown, it has to be the live one: use Trustpilot's own TrustBox widget, which
 * updates itself, rather than typing a number in here that will go stale.
 */
export const TRUSTPILOT_PROFILE_URL =
  "https://uk.trustpilot.com/review/ukweightloss.co.uk";

/**
 * How many five-star reviews the profile has, shown in the hero.
 *
 * A count of five-star reviews is a plain fact the profile backs up, which the
 * old "4.9" was not. Shown as "29+": five-star reviews only accumulate, so the
 * figure stays true as new ones arrive, but raise it when they do. Never pair
 * it with wording that implies an overall rating ("rated 5 stars", "Excellent").
 *
 * Checked 25 October 2026: 32 reviews, 91% five star. 29 is the only count of
 * 32 that rounds to 91%.
 */
export const TRUSTPILOT_FIVE_STAR_COUNT = 29;

/** Opens Trustpilot's "write a review" form for this business directly. */
export const TRUSTPILOT_REVIEW_URL =
  "https://uk.trustpilot.com/evaluate/ukweightloss.co.uk";

export interface TrustpilotReview {
  name: string;
  /** Month and year it was posted on Trustpilot, as shown there. */
  posted: string;
  stars: 1 | 2 | 3 | 4 | 5;
  /**
   * The opening of the review, copied verbatim, stopping at a sentence end.
   * Never edit the wording, fix the spelling or join sentences from different
   * parts of a review: a quote presented as a customer's must be what they
   * wrote.
   */
  excerpt: string;
}

/**
 * A selection of five-star reviews from the profile, labelled as a selection
 * wherever it appears.
 *
 * Chosen with two rules besides the rating:
 *
 *  - No medicine names. Mounjaro and semaglutide are prescription-only, and
 *    a patient naming one in a quote we publish is still us advertising it.
 *    Several of the reviews do name them; none of these do.
 *  - Nothing about other businesses or disputes, so each one reads cleanly
 *    out of context.
 *
 * Check the wording against Trustpilot when adding or changing one. The page
 * sits behind a bot check, so these were not copied programmatically.
 */
export const FEATURED_TRUSTPILOT_REVIEWS: TrustpilotReview[] = [
  {
    name: "Adriana G.",
    posted: "August 2025",
    stars: 5,
    excerpt:
      "I sincerely want to thank this clinic for their dedication and professionalism.",
  },
  {
    name: "Kat",
    posted: "June 2024",
    stars: 5,
    excerpt:
      "So simple, straightforward & extremely fast service. Received my medication next working day as a first time customer.",
  },
  {
    name: "Hollar",
    posted: "August 2024",
    stars: 5,
    excerpt:
      "I have been using UKweightloss for over 6 months and the service is great!",
  },
  {
    name: "Dexter",
    posted: "March 2024",
    stars: 5,
    excerpt:
      "Just placed my second order. Very professional company. Takes the time to answer all my questions.",
  },
  {
    name: "Lana B.",
    posted: "August 2023",
    stars: 5,
    excerpt:
      "A genuine company that sells genuine products and ships quickly and safely maintaining cold-chain. I recommend them!",
  },
  {
    name: "Tasha S.",
    posted: "July 2023",
    stars: 5,
    excerpt:
      "This was an excellent service. Explained every step of the way! No pressure.",
  },
];
